import { createReducer } from "@reduxjs/toolkit";

import { assert } from "../../../asserts/index.ts";
import type { ContentLanguage } from "../../../schemas/index.ts";
import { InvalidActionError } from "../../InvalidActionError.ts";
import { removeLanguageAction } from "../../reducer/index.ts";
import { addContentLanguageAction, updateContentLanguageAction } from "./contentLanguagesActions.ts";

const initialState: ContentLanguage[] = [];

export const contentLanguagesReducer = createReducer(initialState, (builder) => {
  builder.addCase(addContentLanguageAction, (draft, { payload: contentLanguage }) => {
    assert(
      !draft.find((lang) => lang.code === contentLanguage.code),
      `Content language with code ${contentLanguage.code} already exists.`,
    );
    draft.push(contentLanguage);
  });

  builder.addCase(removeLanguageAction, (draft, action) => {
    const { lang: code } = action.payload;
    const existing = draft.find((lang) => lang.code === code);
    if (existing?.isPrimary) {
      throw new InvalidActionError(action, `Content language ${code} is the primary language and cannot be removed.`);
    }
    // mirrors the server: a language falling back to the removed one takes over its fallback, closing the chain up
    const inheritedBaseCode = existing?.baseCode ?? null;
    return draft
      .filter((lang) => lang.code !== code)
      .map((lang) => (lang.baseCode === code ? { ...lang, baseCode: inheritedBaseCode } : lang));
  });

  builder.addCase(updateContentLanguageAction, (draft, { payload: { code, baseCode } }) => {
    const existing = draft.find((lang) => lang.code === code);
    assert(existing, `Content language with code ${code} does not exist.`);
    existing.baseCode = baseCode;
  });
});
